/**
 * Readable pay-stub card for an IMPORTED Knit stub (PDF or CSV). Shows the
 * scanned amounts the way the paper stub lays them out — earnings,
 * withholdings, summary, YTD — with a blank-stub warning when Knit issued a
 * $0 stub, and a button to download the original file as uploaded.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Download, FileSpreadsheet, FileText, Loader2, Pencil, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const hours = (v) => (v == null ? "—" : Number(v).toFixed(2));

const Row = ({ label, cur, ytd, strong, testId }) => (
  <tr className={strong ? "font-bold" : ""} data-testid={testId}>
    <td className="py-1 pr-3 text-gray-700 dark:text-white/70">{label}</td>
    <td className="py-1 pr-3 text-right font-mono tabular-nums text-gray-900 dark:text-white">{cur}</td>
    <td className="py-1 text-right font-mono tabular-nums text-gray-600 dark:text-white/55">{ytd}</td>
  </tr>
);

/** Imported stubs print ONE combined CPP line; an admin types how much of it is
 *  CPP2 so the T4 fills box 16A instead of leaving it inside box 16. */
const Cpp2Row = ({ stub, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const cpp2 = val.trim() === "" ? null : Number(val);
      const r = await api.patch(`/payroll/imported-paystubs/${stub.import_id}`, { cpp2 });
      onSaved(r.data.cpp2);
      setEditing(false);
      toast.success(cpp2 == null ? "CPP2 cleared" : `CPP2 set to ${money(cpp2)} — box 16A updated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save CPP2"); }
    finally { setSaving(false); }
  };
  const base = stub.cpp2 == null ? null : Math.max(0, Number(stub.cpp) - Number(stub.cpp2));
  return (
    <tr data-testid="imported-stub-cpp2">
      <td className="py-1 pr-3 text-gray-700 dark:text-white/70">
        <span>CPP2 share (T4 box 16A)</span>
        {stub.cpp2 == null && !editing && (
          <span className="ml-1.5 rounded bg-[#5a4208] px-1 py-0.5 font-mono text-[8px] font-bold uppercase text-[#fde68a]" data-testid="imported-stub-cpp2-missing">not entered</span>
        )}
      </td>
      <td className="py-1 text-right font-mono tabular-nums text-gray-900 dark:text-white" colSpan={2}>
        {editing ? (
          <span className="inline-flex items-center gap-1">
            <input type="number" min="0" step="0.01" max={stub.cpp ?? undefined} value={val} autoFocus
              onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              data-testid="imported-stub-cpp2-input" placeholder="0.00"
              className="w-24 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-right text-xs text-gray-900 dark:border-white/20 dark:bg-black dark:text-white" />
            <button type="button" onClick={save} disabled={saving} data-testid="imported-stub-cpp2-save" aria-label="Save CPP2"
              className="rounded bg-[#1a3fd1] p-1 text-white disabled:opacity-50">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}</button>
            <button type="button" onClick={() => setEditing(false)} data-testid="imported-stub-cpp2-cancel" aria-label="Cancel"
              className="rounded border border-gray-300 p-1 text-gray-600 dark:border-white/20 dark:text-white/70"><X className="h-3 w-3" /></button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span data-testid="imported-stub-cpp2-value">{stub.cpp2 == null ? "—" : `${money(stub.cpp2)} · base ${money(base)}`}</span>
            {canEdit && (
              <button type="button" onClick={() => { setVal(stub.cpp2 == null ? "" : String(stub.cpp2)); setEditing(true); }}
                data-testid="imported-stub-cpp2-edit" title="Type the CPP2 portion of this stub's CPP line (fills T4 box 16A)"
                className="rounded border border-gray-300 p-1 text-gray-600 hover:bg-gray-100 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/10"><Pencil className="h-3 w-3" /></button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-4">
    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/45">{title}</p>
    <table className="mt-1 w-full text-xs">
      <thead>
        <tr className="font-mono text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/40">
          <th className="pb-1 text-left font-normal"></th>
          <th className="pb-1 text-right font-normal">Current</th>
          <th className="pb-1 text-right font-normal">YTD</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-white/10">{children}</tbody>
    </table>
  </div>
);

export default function ImportedStubCard({ importId, onClose }) {
  const { user } = useAuth();
  const canEdit = ["owner", "superadmin", "admin"].includes(user?.role);
  const [stub, setStub] = useState(null);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    setStub(null); setErr(null);
    api.get(`/payroll/imported-paystubs/${importId}/detail`)
      .then((r) => { if (alive) setStub(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load that stub"); });
    return () => { alive = false; };
  }, [importId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/payroll/imported-paystubs/${importId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = stub?.label || "paystub";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download the original file");
    } finally {
      setDownloading(false);
    }
  };

  const isCsv = stub?.source === "knit_csv";
  const y = stub?.ytd || {};

  return (
    <div className="fixed inset-0 z-[160] flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-testid="imported-stub-card" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-[#0d0d0d]"
        onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/45">
              Imported pay stub · {isCsv ? "CSV export" : "PDF"}
            </p>
            <h3 className="mt-0.5 truncate text-base font-bold text-gray-900 dark:text-white" data-testid="imported-stub-employee">
              {stub?.employee_name || stub?.employee_name_on_stub || "…"}
              {stub?.employee_status && stub.employee_status !== "Active" && (
                <span className="ml-2 rounded-full bg-gray-200 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-gray-700 dark:bg-white/15 dark:text-white/80">{stub.employee_status}</span>
              )}
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="imported-stub-close"
            className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        {err && <p className="mt-4 text-xs text-red-700 dark:text-red-300" data-testid="imported-stub-error">{err}</p>}
        {!stub && !err && (
          <p className="mt-6 flex items-center gap-2 text-xs text-gray-600 dark:text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        )}

        {stub && (
          <>
            {/* identity strip */}
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-[11px] dark:border-white/10 dark:bg-white/[0.04] sm:grid-cols-3">
              <div><p className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/40">Pay date</p><p className="text-gray-900 dark:text-white" data-testid="imported-stub-pay-date">{stub.pay_date}</p></div>
              <div><p className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/40">Pay period</p><p className="text-gray-900 dark:text-white">{stub.period_start && stub.period_end ? `${stub.period_start} → ${stub.period_end}` : "—"}</p></div>
              <div><p className="text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/40">Stub No</p><p className="text-gray-900 dark:text-white">{stub.seq || "—"}</p></div>
            </div>

            {stub.blank && (
              <div className="theme-keep-bg mt-3 rounded-lg border border-[#d97706] bg-[#fef3c7] px-3 py-2 text-xs text-[#451a03] dark:border-[#fbbf24]/60 dark:bg-[#3b2f0b] dark:text-[#fde68a]" data-testid="imported-stub-blank">
                <p className="font-bold uppercase tracking-wider">Blank stub — $0 pay</p>
                <p className="mt-0.5 opacity-90">This imported stub has no earnings on it. It&apos;s recorded so the period isn&apos;t treated as missing, but it adds nothing to the yearly totals.</p>
              </div>
            )}

            <Section title="Earnings">
              <Row label={`Regular pay${stub.regular_rate ? ` · ${money(stub.regular_rate)}/h` : ""}`}
                cur={`${hours(stub.regular_hours)} h · ${money(stub.regular_pay)}`}
                ytd={`${hours(y.regular_hours)} h · ${money(y.regular_pay)}`} testId="imported-stub-regular" />
              <Row label="Vacation pay" cur={money(stub.vacation_pay)} ytd={money(y.vacation_pay)} />
              <Row label="Total gross" cur={money(stub.gross)} ytd={money(y.gross)} strong testId="imported-stub-gross" />
            </Section>

            <Section title="Tax withholdings">
              <Row label="Employee EI" cur={money(stub.ei)} ytd={money(y.ei)} />
              <Row label="Employee CPP (CPP + CPP2 combined)" cur={money(stub.cpp)} ytd={money(y.cpp)} />
              <Cpp2Row stub={stub} canEdit={canEdit} onSaved={(cpp2) => setStub((s) => ({ ...s, cpp2 }))} />
              <Row label="Federal tax" cur={money(stub.federal_tax)} ytd={money(y.federal_tax)} />
              <Row label="Provincial (ON) tax" cur={money(stub.provincial_tax)} ytd={money(y.provincial_tax)} />
              <Row label="Total deductions" cur={money(stub.total_deductions)} ytd={money(y.total_deductions)} strong />
            </Section>

            <Section title="Summary">
              <Row label="Gross" cur={money(stub.gross)} ytd={money(y.gross)} />
              <Row label="Deductions" cur={money(stub.total_deductions)} ytd={money(y.total_deductions)} />
              {stub.reimbursement != null && <Row label="Reimbursement" cur={money(stub.reimbursement)} ytd="—" />}
              <Row label="Net pay" cur={money(stub.net)} ytd={money(y.net)} strong testId="imported-stub-net" />
            </Section>

            {/* footer */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-white/10">
              <p className="flex items-center gap-1.5 font-mono text-[10px] text-gray-500 dark:text-white/45">
                {isCsv ? <FileSpreadsheet className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                {stub.original_filename || stub.label}
                {stub.uploaded_by ? ` · imported by ${stub.uploaded_by}` : ""}
              </p>
              <button type="button" onClick={download} disabled={downloading} data-testid="imported-stub-download"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Download original {isCsv ? "CSV" : "PDF"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
